import React, { useState, useEffect } from 'react';
import useApiGet from '../hooks/getData';
import useApiPost from '../hooks/postData';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import NoData from '../../assets/NoData.png';

function WeeklyReset() {
  const { getData } = useApiGet();
  const { postData } = useApiPost();
  const isSidebarOpen = useSelector((state) => state.sidebar.isOpen);

  const [preview, setPreview] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const res = await getData('/rooms/coin-reset/preview');
      setPreview(res?.data?.hosts || []);
      setGrandTotal(res?.data?.grand_total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await getData('/rooms/coin-reset/history');
      setHistory(res?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchPreview();
    fetchHistory();
  }, []);

  const handleRunReset = async () => {
    setResetting(true);
    try {
      const res = await postData('/rooms/coin-reset/run', {});
      if (res?.success) {
        toast.success(res.message || 'Weekly reset completed!');
        setConfirmOpen(false);
        fetchPreview();
        fetchHistory();
      } else {
        toast.error(res?.message || 'Reset failed');
      }
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className={`dark:bg-primary ${isSidebarOpen ? "xl:pl-20" : "xl:pl-72"}`}>
      <div className="px-4 pb-10 pt-6 xl:px-6">

        <div className="flex items-center justify-between pb-5">
          <h2 className="text-[#000000] font-semibold font-poppins text-xl dark:text-darkText">
            Weekly Coin Reset
          </h2>
      
        </div>

        {/* Pending Preview */}
        <div className="border border-opacity-gradient dark:border-[#1F1F1F] bg-[#FFFFFF] dark:bg-primary rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between pb-4">
            <h3 className="text-base font-semibold font-poppins text-header">Pending Coins (Not Yet Reset)</h3>
            <span className="px-4 py-1.5 text-sm font-semibold text-[#E89A00] bg-[#E89A00] bg-opacity-15 rounded-lg font-poppins">
              Grand Total: 🪙 {grandTotal}
            </span>
          </div>

          {loading ? (
            <p className="py-6 text-sm text-center font-poppins dark:text-darkText">Loading...</p>
          ) : preview.length > 0 ? (
            <div className="overflow-hidden border border-gray-200 dark:border-[#1F1F1F] rounded-lg">
              <div className="flex px-4 py-3 bg-[#00000005] dark:bg-[#1F1F1F] text-sm font-semibold font-poppins text-[#5B5B5B] dark:text-tableDarkLarge">
                <div className="w-[45%]">Host</div>
                <div className="w-[25%] text-center">Gifts Received</div>
                <div className="w-[30%] text-center">Coins to Transfer</div>
              </div>
              {preview.map((h) => (
                <div key={h.host_id} className="flex items-center px-4 py-3 border-t border-gray-100 dark:border-[#1F1F1F]">
                  <div className="w-[45%] font-poppins text-sm dark:text-darkText">{h.host_name || `User #${h.host_id}`}</div>
                  <div className="w-[25%] text-center font-poppins text-sm dark:text-darkText">{h.gift_count}</div>
                  <div className="w-[30%] text-center">
                    <span className="px-3 py-1 text-sm font-semibold text-[#0D9947] bg-[#0D9947] bg-opacity-15 rounded-lg font-poppins">
                      🪙 {h.total_coins}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-6 text-sm text-center font-poppins text-[#777777]">Nothing pending — all caught up!</p>
          )}
        </div>

        {/* Past Resets History */}
        <div className="border border-opacity-gradient dark:border-[#1F1F1F] bg-[#FFFFFF] dark:bg-primary rounded-lg p-6">
          <h3 className="text-base font-semibold font-poppins text-header pb-4">Reset History</h3>
          {history.length > 0 ? (
            <div className="overflow-hidden border border-gray-200 dark:border-[#1F1F1F] rounded-lg">
              <div className="flex px-4 py-3 bg-[#00000005] dark:bg-[#1F1F1F] text-sm font-semibold font-poppins text-[#5B5B5B] dark:text-tableDarkLarge">
                <div className="w-[30%]">Host</div>
                <div className="w-[25%] text-center">Coins Paid</div>
                <div className="w-[20%] text-center">Gifts</div>
                <div className="w-[25%]">Reset Date</div>
              </div>
              {history.map((h) => (
                <div key={h.id} className="flex items-center px-4 py-3 border-t border-gray-100 dark:border-[#1F1F1F]">
                  <div className="w-[30%] font-poppins text-sm dark:text-darkText">{h.host_name || `User #${h.host_id}`}</div>
                  <div className="w-[25%] text-center font-poppins text-sm font-semibold text-[#0D9947]">🪙 {h.total_coins}</div>
                  <div className="w-[20%] text-center font-poppins text-sm dark:text-darkText">{h.gift_count}</div>
                  <div className="w-[25%] font-poppins text-xs text-[#777777]">
                    {new Date(h.reset_at.replace(' ', 'T')).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <img src={NoData} alt="no data" className="w-72" />
              <p className="text-sm font-poppins text-[#777777]">No resets done yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Confirm Dialog */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white dark:bg-primary rounded-lg p-6 w-[90%] max-w-md">
            <h3 className="text-lg font-semibold font-poppins text-[#000000] dark:text-darkText pb-2">
              Confirm Weekly Reset
            </h3>
            <p className="text-sm font-poppins text-[#777777] pb-5">
              This will transfer 🪙 {grandTotal} coins to {preview.length} host(s)' wallets and reset all room counters. This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmOpen(false)}
                className="px-5 py-2 text-sm font-poppins border border-gray-300 rounded-lg dark:text-darkText"
              >
                Cancel
              </button>
              <button
                onClick={handleRunReset}
                disabled={resetting}
                style={{ backgroundColor: '#D92D20' }}
                className="px-5 py-2 text-sm font-poppins text-white rounded-lg"
              >
                {resetting ? "Processing..." : "Yes, Reset Now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WeeklyReset;